/* eslint-disable import/prefer-default-export */
import queryString from 'query-string';
import { generatePath } from 'react-router-dom';

import {
  API_ADGROUP_LOCATION_BATCH_CREATE_URL,
  API_ADGROUP_LOCATION_BATCH_UPDATE_URL,
  API_CREATE_LOCATION_FILE_URL,
  API_HELPERS_BATCH_JOB_PROGRESS,
  API_LOCATION_DESTROY_ALL_URL,
  API_LOCATION_URL,
  API_LOCATIONS_AUTOCOMPLETE_URL,
  API_LOCATIONS_URL,
} from '../../constants/apiUrls';
import { Location } from '../../constants/locationTable';
import { PageableList } from '../../types/contract';
import { snakeCaseKeys } from '../../util/object';
import { deleteRequestJSON, getRequestJSON, postRequestJSON, putRequestJSON } from '../../util/services';

interface QueryParams {
  page?: number;
  perPage?: number;
}

export const getAll = (
  apiHost: string,
  adGroupId: number,
  excluded: boolean,
  term: string,
  { page, perPage }: QueryParams
): Promise<PageableList<Location>> => {
  const queryParams = `?${queryString.stringify({
    page: page + 1,
    per_page: perPage,
    excluded: excluded ? true : undefined,
    term,
  })}`;
  return getRequestJSON(
    apiHost,
    generatePath(API_LOCATIONS_URL, { adGroupId: adGroupId.toString(), '*': queryParams })
  );
};

export const create = (apiHost: string, adGroupId: number, location: Location, signal?: AbortSignal): Promise<any> =>
  postRequestJSON(
    apiHost,
    generatePath(API_LOCATIONS_URL, { adGroupId: adGroupId.toString(), '*': '' }),
    location,
    undefined,
    { signal }
  );

export const update = (apiHost: string, adGroupId: number, location: Location, signal?: AbortSignal): Promise<any> => {
  const queryParams = `?${queryString.stringify({
    campaign_id: adGroupId,
  })}`;
  return putRequestJSON(
    apiHost,
    generatePath(API_LOCATION_URL, { locationId: location.id.toString(), '*': queryParams }),
    location,
    undefined,
    { signal }
  );
};

export const remove = (apiHost: string, locationId: number, signal?: AbortSignal): Promise<any> =>
  deleteRequestJSON(apiHost, generatePath(API_LOCATION_URL, { locationId: locationId.toString(), '*': '' }), {
    signal,
  });

export const removeAll = (apiHost: string, adGroupId: number, signal?: AbortSignal): Promise<any> =>
  deleteRequestJSON(apiHost, generatePath(API_LOCATION_DESTROY_ALL_URL, { adGroupId: adGroupId.toString() }), {
    signal,
  });

export const batchAdd = (apiHost: string, adGroupId: number, payload): Promise<any> =>
  postRequestJSON(
    apiHost,
    generatePath(API_ADGROUP_LOCATION_BATCH_CREATE_URL, {
      adGroupId: adGroupId.toString(),
    }),
    payload
  );

export const batchUpdate = (apiHost: string, adGroupId: number, payload): Promise<any> =>
  postRequestJSON(
    apiHost,
    generatePath(API_ADGROUP_LOCATION_BATCH_UPDATE_URL, {
      adGroupId: adGroupId.toString(),
    }),
    payload
  );

export const batchProgress = (apiHost: string, statusFilter): Promise<any> =>
  postRequestJSON(
    apiHost,
    API_HELPERS_BATCH_JOB_PROGRESS,
    snakeCaseKeys({
      statusFilter,
    })
  );

export const createLocationFile = (apiHost: string, payload): Promise<any> =>
  postRequestJSON(apiHost, API_CREATE_LOCATION_FILE_URL, payload);

export const searchLocationType = (apiHost: string, locationType: string, term: string): Promise<any> =>
  getRequestJSON(
    apiHost,
    generatePath(API_LOCATIONS_AUTOCOMPLETE_URL, {
      locationType: `autocomplete_${locationType}`,
      '*': `?${queryString.stringify({
        term,
        poiSourceId: 1,
      })}`,
    })
  );
